import { useCallback, useEffect, useRef, useState } from 'react';

import { AddOutlined, ChevronRight } from '@mui/icons-material';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import httpInstances from '@/api/axiosInstance';
import { dashboardQueryKeys } from '@/features/Dashboard/api/queries';
import { useWidgetAnimation } from '@/features/Dashboard/context/WidgetAnimation/hooks';
import { getTabContent } from '@/features/Dashboard/lib/transformers';
import { DashboardTab, MarketApps } from '@/features/Dashboard/types';
import { getUniqueDashboardName } from '@/features/Dashboard/utils/getUniqueDashboardName';
import { getUniqueTabName } from '@/features/Dashboard/utils/getUniqueTabName';
import { useDashboard } from '@/hooks/api/dashboard';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import WidgetAdapter from '@/pages/Markets/utils/WidgetAdapter';
import { BaseTabType } from '@/slices/marketsSlice';

interface AddToDashboardMenuProps {
  tab: BaseTabType;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const menuItemStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  p: 1,
  pr: 2,
  fontSize: 12,
  fontWeight: 600,
  height: '34px',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  }
};

const activeMenuItemStyles = {
  ...menuItemStyles,
  backgroundColor: 'rgba(255, 255, 255, 0.08)'
};

const ADD_DASHBOARD_APP_SNACKBAR_KEY = 'add-dashboard-app';

const AddToDashboardMenu = ({ tab, anchorEl, open, onClose }: AddToDashboardMenuProps) => {
  const { data: allDashboards } = useDashboard();
  const { setElementsToAnimateId } = useWidgetAnimation();
  const { showSuccessSnackbar, showErrorSnackbar, showLoadingSnackbar } =
    useSnackbarNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [hoveredDashboard, setHoveredDashboard] = useState<string | null>(null);
  const dashboardItemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const subMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigationTargetRef = useRef<{
    dashboardName: string;
    tabName: string;
    widgetId: string;
  } | null>(null);

  // Generic mutation for adding widgets to any dashboard/tab
  const { mutate: addWidgetToDashboard, isPending } = useMutation({
    mutationFn: async ({
      dashboardName,
      tabName,
      widgetId,
      payload
    }: {
      dashboardName: string;
      tabName: string;
      widgetId: string;
      payload: DashboardTab;
    }) => {
      // Store navigation target and widget ID for onSuccess
      navigationTargetRef.current = { dashboardName, tabName, widgetId };

      const response = await httpInstances.axiosInternalInstance.post(
        `/config/dashboard/${encodeURIComponent(dashboardName)}`,
        payload
      );
      await new Promise<void>(resolve => {
        setTimeout(() => {
          resolve();
        }, 2000);
      });
      return response;
    },
    onMutate: () => {
      showLoadingSnackbar('Adding app to dashboard...', ADD_DASHBOARD_APP_SNACKBAR_KEY);
    },
    onSuccess: async () => {
      showSuccessSnackbar('App added to dashboard!', ADD_DASHBOARD_APP_SNACKBAR_KEY);

      // Wait for the dashboard query to be refetched before navigating
      await queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });

      // Set widget animation and navigate to the dashboard and tab
      if (navigationTargetRef.current) {
        const { dashboardName, tabName, widgetId } = navigationTargetRef.current;
        setElementsToAnimateId([widgetId]);
        navigate(`/dashboard/${encodeURIComponent(dashboardName)}/${encodeURIComponent(tabName)}`);
        navigationTargetRef.current = null;
      }
    },
    onError: () => {
      showErrorSnackbar(
        "Couldn't add app to dashboard. Please try again.",
        undefined,
        ADD_DASHBOARD_APP_SNACKBAR_KEY
      );
      navigationTargetRef.current = null;
    }
  });

  const createWidget = useCallback(() => {
    let name = tab.name;
    if (!name && tab.type === 'feed') {
      name = 'Feed';
    }
    return WidgetAdapter.newWidget({ ...tab, name });
  }, [tab]);

  const handleAddToTab = useCallback(
    (dashboardName: string, tabName: string, existingTabs: DashboardTab) => {
      if (isPending) return;

      const widget = createWidget();
      if (!widget) return;

      const existingTabValue = existingTabs[tabName];
      const existingApps = existingTabValue ? getTabContent(existingTabValue) : [];
      const updatedApps = [...existingApps, widget] as MarketApps;

      // Build the full dashboard structure to preserve all tabs
      const payload: DashboardTab = {
        ...existingTabs,
        [tabName]: updatedApps
      };

      addWidgetToDashboard({
        dashboardName,
        tabName,
        widgetId: widget.id || '',
        payload
      });
      onClose();
    },
    [isPending, createWidget, addWidgetToDashboard, onClose]
  );

  const handleCreateNewDashboard = useCallback(() => {
    if (isPending) return;

    const widget = createWidget();
    if (!widget) return;

    // Generate unique dashboard name
    const existingNames = Object.keys(allDashboards || {});
    const newName = getUniqueDashboardName(existingNames);

    // Create new dashboard with Main tab containing the widget
    const payload: DashboardTab = {
      Main: [widget] as MarketApps
    };

    addWidgetToDashboard({
      dashboardName: newName,
      tabName: 'Main',
      widgetId: widget.id || '',
      payload
    });
    onClose();
  }, [isPending, createWidget, allDashboards, addWidgetToDashboard, onClose]);

  const handleCreateNewTab = useCallback(
    (dashboardName: string, existingTabs: DashboardTab) => {
      if (isPending) return;

      const widget = createWidget();
      if (!widget) return;

      // Generate unique tab name
      const existingTabNames = Object.keys(existingTabs);
      const newTabName = getUniqueTabName(existingTabNames);

      // Build the full dashboard structure with new tab
      const payload: DashboardTab = {
        ...existingTabs,
        [newTabName]: [widget] as MarketApps
      };

      addWidgetToDashboard({
        dashboardName,
        tabName: newTabName,
        widgetId: widget.id || '',
        payload
      });
      onClose();
    },
    [isPending, createWidget, addWidgetToDashboard, onClose]
  );

  const handleDashboardMouseEnter = useCallback((dashboardName: string) => {
    if (subMenuTimeoutRef.current) {
      clearTimeout(subMenuTimeoutRef.current);
    }
    setHoveredDashboard(dashboardName);
  }, []);

  const handleDashboardMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      setHoveredDashboard(null);
    }, 150);
  }, []);

  const handleSubMenuMouseEnter = useCallback(() => {
    if (subMenuTimeoutRef.current) {
      clearTimeout(subMenuTimeoutRef.current);
    }
  }, []);

  const handleSubMenuMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      setHoveredDashboard(null);
    }, 150);
  }, []);

  const dashboardNames = Object.keys(allDashboards || {});
  const hoveredDashboardTabs = hoveredDashboard ? allDashboards?.[hoveredDashboard] : null;
  const [hoveredDashboardRef, setHoveredDashboardRef] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (hoveredDashboard) {
      const ref = dashboardItemRefs.current.get(hoveredDashboard);
      // Use setTimeout to avoid calling setState synchronously in effect
      timeout = setTimeout(() => {
        setHoveredDashboardRef(ref || null);
      }, 0);
    } else {
      timeout = setTimeout(() => {
        setHoveredDashboardRef(null);
      }, 0);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [hoveredDashboard]);

  return (
    <Popper
      anchorEl={anchorEl}
      open={open}
      placement='bottom-start'
      sx={{ zIndex: 10000 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          sx={{
            backgroundColor: 'secondary.main',
            minWidth: 200
          }}
        >
          {/* Dashboard List */}
          {dashboardNames.map(dashboardName => (
            <Box
              key={dashboardName}
              ref={(el: HTMLDivElement | null) => {
                if (el) {
                  dashboardItemRefs.current.set(dashboardName, el);
                } else {
                  dashboardItemRefs.current.delete(dashboardName);
                }
              }}
              sx={hoveredDashboard === dashboardName ? activeMenuItemStyles : menuItemStyles}
              onMouseEnter={() => {
                handleDashboardMouseEnter(dashboardName);
              }}
              onMouseLeave={handleDashboardMouseLeave}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600
                }}
              >
                {dashboardName}
              </Typography>
              <ChevronRight sx={{ fontSize: 20, opacity: 0.7 }} />
            </Box>
          ))}

          {/* New Dashboard Option */}
          <Box
            sx={{
              ...menuItemStyles
            }}
            onClick={handleCreateNewDashboard}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddOutlined sx={{ fontSize: 18 }} />
              <Typography
                fontSize={12}
                sx={{ color: 'text.secondary' }}
              >
                New Dashboard
              </Typography>
            </Box>
          </Box>
        </Paper>
      </ClickAwayListener>

      {/* Sub-menu for tabs */}
      {hoveredDashboard && hoveredDashboardTabs && hoveredDashboardRef && (
        <Popper
          open
          anchorEl={hoveredDashboardRef}
          placement='right-start'
          sx={{ zIndex: 10001 }}
        >
          <Paper
            sx={{
              backgroundColor: 'secondary.main',
              minWidth: 180
            }}
            onMouseEnter={handleSubMenuMouseEnter}
            onMouseLeave={handleSubMenuMouseLeave}
          >
            {/* Tab List */}
            {Object.keys(hoveredDashboardTabs).map(tabName => (
              <Box
                key={tabName}
                sx={menuItemStyles}
                onClick={() => {
                  handleAddToTab(hoveredDashboard, tabName, hoveredDashboardTabs);
                }}
              >
                {tabName}
              </Box>
            ))}

            {/* New Tab Option */}
            <Box
              sx={{
                ...menuItemStyles
              }}
              onClick={() => {
                handleCreateNewTab(hoveredDashboard, hoveredDashboardTabs);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddOutlined sx={{ fontSize: 18 }} />
                <Typography
                  fontSize={12}
                  sx={{ color: 'text.secondary' }}
                >
                  New page
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Popper>
      )}
    </Popper>
  );
};

export default AddToDashboardMenu;
